import { ProgressCard } from "../common/ProgressCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { GenderPayGapCalculatorModal } from "../common/GenderPayGapCalculatorModal";
import { Textarea } from "@/components/ui/textarea";
import { useState, useEffect, type ReactNode } from "react";
import { AlertTriangle, HelpCircle, Info } from "lucide-react";
import { InfoCallout } from "../common/InfoCallout";
import { useWizard } from "@/contexts/WizardContext";
import { useToast } from "@/hooks/use-toast";
import { LockedBanner } from "../common/LockedBanner";
import { CardPanelHeader } from "../common/CardPanelHeader";
import { SectionCard } from "../common/SectionCard";
import { TransitionMeasuresBlock } from "@/components/workflow/common/TransitionMeasuresBlock";
import type { EmployeeSupportData } from "@/contexts/wizard/types";
import { cn } from "@/lib/utils";

interface EmployeeSupportCardProps {
  onCollapse: () => void;
}

function stripSectionCode(title: string) {
  return title.replace(/^B\d+\s*[:\-]\s*/i, "").trim();
}

function FieldBlock({
  label,
  required,
  hint,
  children,
}: {
  label: ReactNode;
  required?: boolean;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <Label className="form-label ">
          {label}
          {required ? <span className="ml-1 text-red-500">*</span> : null}
        </Label>
        {hint ? <span className="text-xs leading-4 text-slate-500">{hint}</span> : null}
      </div>
      {children}
    </div>
  );
}

function ValidationBanner({
  tone,
  children,
}: {
  tone: "warning" | "error";
  children: ReactNode;
}) {
  return (
    <p
      className={cn(
        "flex items-start gap-2 rounded-lg border px-3 py-2 text-sm",
        tone === "error"
          ? "border-red-200 bg-red-50 text-red-700"
          : "border-amber-200 bg-amber-50 text-amber-700",
      )}
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{children}</span>
    </p>
  );
}

function ChoiceCard({
  checked,
  disabled,
  label,
  name,
  id,
  onChange,
}: {
  checked: boolean;
  disabled: boolean;
  label: string;
  name: string;
  id: string;
  onChange: () => void;
}) {
  return (
    <label
      htmlFor={id}
      className={cn(
        "flex flex-1 cursor-pointer items-center gap-3 rounded-2xl border p-4 transition-colors",
        checked ? "border-primary bg-primary/5" : "border-slate-200 bg-white",
        disabled && "cursor-not-allowed opacity-60",
      )}
    >
      <input
        id={id}
        type="radio"
        name={name}
        className="sr-only"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <span
        aria-hidden="true"
        className={cn(
          "flex h-4 w-4 items-center justify-center rounded-full border bg-white",
          checked ? "border-primary" : "border-slate-400",
        )}
      >
        <span className={cn("h-2 w-2 rounded-full bg-primary", !checked && "opacity-0")} />
      </span>
      <span className="text-sm font-medium leading-5 text-slate-900">{label}</span>
    </label>
  );
}

export function EmployeeSupportCard({ onCollapse }: EmployeeSupportCardProps) {
  const {
    employeeSupportData,
    updateEmployeeSupportData,
    workforceCompletion,
    forceSave,
    saveStatus,
    isLocked,
    isReadOnly,
    assessmentId,
  } = useWizard();
  const disabled = isLocked || isReadOnly;
  const { toast } = useToast();

  const [generalLimits, setGeneralLimits] = useState<{
    total: number | null;
    female: number | null;
    male: number | null;
  } | undefined>(undefined);
  const [exceedsError, setExceedsError] = useState(false);
  const [femaleExceedsError, setFemaleExceedsError] = useState(false);
  const [maleExceedsError, setMaleExceedsError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [calculatedViaModal, setCalculatedViaModal] = useState(false);

  useEffect(() => {
    if (!assessmentId) return;
    fetch(`/api/assessments/${assessmentId}/general/total-employees`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) =>
        setGeneralLimits({
          total: data.totalEmployees ?? null,
          female: data.femaleEmployees ?? null,
          male: data.maleEmployees ?? null,
        }),
      )
      .catch(() => setGeneralLimits({ total: null, female: null, male: null }));
  }, [assessmentId]);

  const permanentCount = parseInt(employeeSupportData.permanentEmployees) || 0;
  const temporaryCount = parseInt(employeeSupportData.temporaryEmployees) || 0;
  const femaleCount = parseInt(employeeSupportData.femaleEmployees) || 0;
  const maleCount = parseInt(employeeSupportData.maleEmployees) || 0;

  const handleWorkforceChange = (updates: Partial<EmployeeSupportData>) => {
    const total = generalLimits?.total;
    if (typeof total === "number" && total > 0) {
      const newPermanent =
        updates.permanentEmployees !== undefined
          ? parseInt(updates.permanentEmployees) || 0
          : permanentCount;
      const newTemporary =
        updates.temporaryEmployees !== undefined
          ? parseInt(updates.temporaryEmployees) || 0
          : temporaryCount;
      if (newPermanent + newTemporary > total) {
        setExceedsError(true);
        return;
      }
      setExceedsError(false);
    }
    updateEmployeeSupportData(updates);
  };

  const handleGenderChange = (updates: Partial<EmployeeSupportData>) => {
    const newFemale =
      updates.femaleEmployees !== undefined
        ? parseInt(updates.femaleEmployees) || 0
        : femaleCount;
    const newMale =
      updates.maleEmployees !== undefined ? parseInt(updates.maleEmployees) || 0 : maleCount;

    if (typeof generalLimits?.female === "number" && newFemale > generalLimits.female) {
      setFemaleExceedsError(true);
      return;
    }
    setFemaleExceedsError(false);

    if (typeof generalLimits?.male === "number" && newMale > generalLimits.male) {
      setMaleExceedsError(true);
      return;
    }
    setMaleExceedsError(false);

    updateEmployeeSupportData(updates);
  };

  const hasTotal = typeof generalLimits?.total === "number" && generalLimits.total > 0;
  const permanentEntered = String(employeeSupportData.permanentEmployees ?? "").trim() !== "";
  const temporaryEntered = String(employeeSupportData.temporaryEmployees ?? "").trim() !== "";
  const femaleEntered = String(employeeSupportData.femaleEmployees ?? "").trim() !== "";
  const maleEntered = String(employeeSupportData.maleEmployees ?? "").trim() !== "";

  const workforceMismatch =
    hasTotal &&
    !exceedsError &&
    (permanentEntered || temporaryEntered) &&
    permanentCount + temporaryCount !== generalLimits!.total!;

  const femaleMismatch =
    typeof generalLimits?.female === "number" &&
    !femaleExceedsError &&
    femaleEntered &&
    femaleCount !== generalLimits.female;

  const maleMismatch =
    typeof generalLimits?.male === "number" &&
    !maleExceedsError &&
    maleEntered &&
    maleCount !== generalLimits.male;

  const saveBlocked =
    generalLimits?.total === null ||
    exceedsError ||
    femaleExceedsError ||
    maleExceedsError;

  const handleSave = async () => {
    const saved = await forceSave();
    if (saved) {
      const hasConsistencyIssue = workforceMismatch || femaleMismatch || maleMismatch;
      if (hasConsistencyIssue) {
        toast({
          variant: "destructive",
          title: "Saved with inconsistencies",
          description:
            "Employee counts don't match the General tab. Please reconcile before generating your report.",
        });
      } else {
        toast({
          title: "Saved successfully",
          description: "Employee support data has been saved.",
        });
      }
    }
  };

  const workforceTitle = stripSectionCode("B8: Workforce Basics");
  const healthTitle = stripSectionCode("B9: Health & Safety");
  const payTitle = stripSectionCode("B10: Pay, Bargaining & Training");

  function handleUseCalculatedValue(value: string) {
    updateEmployeeSupportData({ genderPayGapPercent: value });
    setCalculatedViaModal(true);
    setModalOpen(false);
  }

  return (
    <div className="flex flex-col gap-6">
      <CardPanelHeader
        title={"Employee Support (B8-B10)"}
        description={"Tell us about your employees — how many you have, their working conditions, pay, and any training you provide"}
        onClose={onCollapse}
        onSave={() => {
          void handleSave();
        }}
        saveStatus={saveStatus}
        disabled={disabled}
        saveDisabled={saveBlocked}
        tag={"Social"}
      />

      {disabled && <LockedBanner />}

      <div className="flex flex-col gap-6">
        <SectionCard
          header={
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-sm font-bold leading-5 text-primary">
                B8
              </div>
              <h3 className="text-lg font-semibold leading-7 text-primary">{workforceTitle}</h3>
              <HelpCircle className="h-[15px] w-[15px] text-primary" />
            </div>
          }
        >
          <div className="flex flex-col gap-4">
            <div className="grid gap-4 md:grid-cols-2">
              <FieldBlock label={"Permanent employees (count)"} required>
                <Input
                  type="number"
                  placeholder="e.g. 12"
                  min="0"
                  value={employeeSupportData.permanentEmployees}
                  onChange={(e) => handleWorkforceChange({ permanentEmployees: e.target.value })}
                  disabled={disabled}
                  className="form-input h-12 rounded-lg px-4 text-base"
                />
              </FieldBlock>

              <FieldBlock label={"Temporary employees (count)"} required>
                <Input
                  type="number"
                  placeholder="e.g. 3"
                  min="0"
                  value={employeeSupportData.temporaryEmployees}
                  onChange={(e) => handleWorkforceChange({ temporaryEmployees: e.target.value })}
                  disabled={disabled}
                  className="form-input h-12 rounded-lg px-4 text-base"
                />
              </FieldBlock>
            </div>

            {generalLimits?.total === null && (
              <ValidationBanner tone="warning">
                {"Total employee count is not set. Please fill it in the"}{" "}
                <strong>{"General tab"}</strong>{" "}
                {"before saving."}
              </ValidationBanner>
            )}
            {exceedsError && (
              <ValidationBanner tone="error">
                {`Permanent + temporary cannot exceed the total employees set in the General tab (${generalLimits?.total}). Value not accepted.`}
              </ValidationBanner>
            )}
            {workforceMismatch && (
              <ValidationBanner tone="warning">
                {`Permanent + temporary (${permanentCount + temporaryCount}) must equal the total employees in the General tab (${generalLimits?.total}).`}
              </ValidationBanner>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <FieldBlock label={"Female employees (count)"} required>
                <Input
                  type="number"
                  placeholder="e.g. 6"
                  min="0"
                  value={employeeSupportData.femaleEmployees}
                  onChange={(e) => handleGenderChange({ femaleEmployees: e.target.value })}
                  disabled={disabled}
                  className="form-input h-12 rounded-lg px-4 text-base"
                />
              </FieldBlock>

              <FieldBlock label={"Male employees (count)"} required>
                <Input
                  type="number"
                  placeholder="e.g. 9"
                  min="0"
                  value={employeeSupportData.maleEmployees}
                  onChange={(e) => handleGenderChange({ maleEmployees: e.target.value })}
                  disabled={disabled}
                  className="form-input h-12 rounded-lg px-4 text-base"
                />
              </FieldBlock>
            </div>

            {femaleExceedsError && (
              <ValidationBanner tone="error">
                {`Female count cannot exceed the General tab value (${generalLimits?.female}). Value not accepted.`}
              </ValidationBanner>
            )}
            {maleExceedsError && (
              <ValidationBanner tone="error">
                {`Male count cannot exceed the General tab value (${generalLimits?.male}). Value not accepted.`}
              </ValidationBanner>
            )}
            {femaleMismatch && (
              <ValidationBanner tone="warning">
                Female employees ({femaleCount}) does not match the General tab ({generalLimits?.female}). Please reconcile before generating your report.
              </ValidationBanner>
            )}
            {maleMismatch && (
              <ValidationBanner tone="warning">
                Male employees ({maleCount}) does not match the General tab ({generalLimits?.male}). Please reconcile before generating your report.
              </ValidationBanner>
            )}

            <FieldBlock label={"Employees by country (optional)"}>
              <Textarea
                placeholder="e.g. Malawi: 12, Kenya: 3"
                value={employeeSupportData.employeesByCountry}
                onChange={(e) => updateEmployeeSupportData({ employeesByCountry: e.target.value })}
                disabled={disabled}
                className="form-input min-h-[60px] rounded-lg px-4 py-3 text-base"
              />
            </FieldBlock>

            <FieldBlock label={"Employee turnover rate (%) - only if 50+ employees"}>
              <Input
                type="number"
                placeholder="e.g. 15.5"
                step="0.1"
                min="0"
                value={employeeSupportData.turnoverRate}
                onChange={(e) => updateEmployeeSupportData({ turnoverRate: e.target.value })}
                disabled={disabled}
                className="form-input h-12 rounded-lg px-4 text-base"
              />
            </FieldBlock>
          </div>
        </SectionCard>

        <SectionCard
          header={
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-sm font-bold leading-5 text-primary">
                B9
              </div>
              <h3 className="text-lg font-semibold leading-7 text-primary">{healthTitle}</h3>
              <HelpCircle className="h-[15px] w-[15px] text-primary" />
            </div>
          }
        >
          <div className="flex flex-col gap-4">
            <div className="grid gap-4 md:grid-cols-2">
              <FieldBlock label={"Recordable accidents (count)"} required>
                <Input
                  type="number"
                  placeholder="e.g. 2"
                  min="0"
                  value={employeeSupportData.recordableAccidents}
                  onChange={(e) => updateEmployeeSupportData({ recordableAccidents: e.target.value })}
                  disabled={disabled}
                  className="form-input h-12 rounded-lg px-4 text-base"
                />
              </FieldBlock>

              <FieldBlock label={"Accident rate"}>
                <Input
                  type="number"
                  placeholder="e.g. 0.5"
                  step="0.1"
                  min="0"
                  value={employeeSupportData.accidentRate}
                  onChange={(e) => updateEmployeeSupportData({ accidentRate: e.target.value })}
                  disabled={disabled}
                  className="form-input h-12 rounded-lg px-4 text-base"
                />
              </FieldBlock>
            </div>

            <FieldBlock label={"Work-related fatalities (count)"} required>
              <Input
                type="number"
                placeholder="0"
                min="0"
                value={employeeSupportData.workRelatedFatalities}
                onChange={(e) =>
                  updateEmployeeSupportData({ workRelatedFatalities: e.target.value })
                }
                disabled={disabled}
                className="form-input h-12 rounded-lg px-4 text-base"
              />
            </FieldBlock>
          </div>
        </SectionCard>

        <SectionCard
          header={
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-sm font-bold leading-5 text-primary">
                B10
              </div>
              <h3 className="text-lg font-semibold leading-7 text-primary">{payTitle}</h3>
              <HelpCircle className="h-[15px] w-[15px] text-primary" />
            </div>
          }
        >
          <div className="flex flex-col gap-4">
            <FieldBlock label={"Are all your employees paid at or above the minimum wage that applies in their location?"} required>
              <div className="flex flex-col gap-4 sm:flex-row">
                <ChoiceCard
                  checked={employeeSupportData.minimumWageCompliance === true}
                  disabled={disabled}
                  label={"Yes"}
                  name="min_wage"
                  id="wage_yes"
                  onChange={() => updateEmployeeSupportData({ minimumWageCompliance: true })}
                />
                <ChoiceCard
                  checked={employeeSupportData.minimumWageCompliance === false}
                  disabled={disabled}
                  label={"No"}
                  name="min_wage"
                  id="wage_no"
                  onChange={() => updateEmployeeSupportData({ minimumWageCompliance: false })}
                />
              </div>
            </FieldBlock>

            <FieldBlock
              label={"% of employees covered by collective bargaining agreements"}
              required
            >
              <Input
                type="number"
                placeholder="e.g. 25"
                step="0.1"
                min="0"
                max="100"
                value={employeeSupportData.collectiveBargainingPercent}
                onChange={(e) =>
                  updateEmployeeSupportData({ collectiveBargainingPercent: e.target.value })
                }
                disabled={disabled}
                className="form-input h-12 rounded-lg px-4 text-base"
              />
            </FieldBlock>

            <div className="flex flex-col gap-4">
              <Label className="text-sm font-medium leading-5 text-primary">
                {"Average annual training hours per employee"}
                <span className="ml-1 text-red-500">*</span>
              </Label>
              <div className="grid gap-4 md:grid-cols-2">
                <FieldBlock label={"Female (hours)"}>
                  <Input
                    type="number"
                    placeholder="e.g. 20"
                    step="0.1"
                    min="0"
                    value={employeeSupportData.trainingHoursFemale}
                    onChange={(e) =>
                      updateEmployeeSupportData({ trainingHoursFemale: e.target.value })
                    }
                    disabled={disabled}
                    className="form-input h-12 rounded-lg px-4 text-base"
                  />
                </FieldBlock>
                <FieldBlock label={"Male (hours)"}>
                  <Input
                    type="number"
                    placeholder="e.g. 20"
                    step="0.1"
                    min="0"
                    value={employeeSupportData.trainingHoursMale}
                    onChange={(e) =>
                      updateEmployeeSupportData({ trainingHoursMale: e.target.value })
                    }
                    disabled={disabled}
                    className="form-input h-12 rounded-lg px-4 text-base"
                  />
                </FieldBlock>
              </div>
            </div>

            <FieldBlock
              label={
                <>
                  Gender pay gap %{" "}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="inline h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      [(Male avg hourly pay − Female avg hourly pay) ÷ Male avg
                      hourly pay] × 100
                    </TooltipContent>
                  </Tooltip>
                </>
              }
            >
              <Input
                type="number"
                placeholder="e.g. 5.5"
                step="0.1"
                value={employeeSupportData.genderPayGapPercent}
                onChange={(e) => {
                  updateEmployeeSupportData({ genderPayGapPercent: e.target.value });
                  setCalculatedViaModal(false);
                }}
                disabled={disabled}
                className="form-input h-12 rounded-lg px-4 text-base"
              />
              <Button
                variant="link"
                size="sm"
                className="px-0 h-auto"
                disabled={disabled}
                onClick={() => setModalOpen(true)}
              >
                Calculate Gender Pay Gap
              </Button>
              {calculatedViaModal && (
                <p className="text-xs text-muted-foreground">
                  Calculated via hourly pay tool
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                This field may be omitted for companies with fewer than 150
                employees. From 7 June 2031, the threshold reduces to 100
                employees.
              </p>
            </FieldBlock>

            <InfoCallout
              label={"Tip:"}
              text={"Use HR/payroll summaries for accurate data"}
            />
          </div>
        </SectionCard>

        <TransitionMeasuresBlock topicId="employee-support" currentTab="social" disabled={disabled} />

        <ProgressCard completion={workforceCompletion} />
      </div>

      <GenderPayGapCalculatorModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        onUseValue={handleUseCalculatedValue}
      />
    </div>
  );
}
